/* ============================================================
   Thin async "API" for the Auto-Dub feature — now backed by the
   real FastAPI endpoints via repositories.jsx. Reads return the
   normalized (camel/mock-shaped) objects the components were
   built against; see mappers.js for the field mapping.
   ============================================================ */
import repositories from '../../repositories.jsx';
import { normalizeVideo, normalizeWorkflow, draftToWorkflowPayload } from './mappers.js';

export const autoDubService = {
	// a channel's uploads (the source library for the picker)
	listVideos: (channelId) => !channelId
		? Promise.resolve([])
		: repositories.channel.videos(channelId).then(list => (list ?? []).map(normalizeVideo)),
	// the channel's playlist titles (Step 2 filter pills)
	listPlaylists: (channelId) => !channelId
		? Promise.resolve([])
		: repositories.channel.playlists(channelId).then(list => (list ?? []).map(p => p.title)),
	// turn a completed draft into a live workflow on the backend
	createWorkflow: (draft) =>
		repositories.autoDub.workflows.create(draftToWorkflowPayload(draft)).then(normalizeWorkflow),
	// persist edits to an existing workflow (same snake_case payload; the backend
	// ignores the immutable type/source/publish_mode keys)
	updateWorkflow: (id, draft) =>
		repositories.autoDub.workflows.update(id, draftToWorkflowPayload(draft)).then(normalizeWorkflow),
};

export const createWorkflow = autoDubService.createWorkflow;
export const updateWorkflow = autoDubService.updateWorkflow;

/* a fresh builder draft (ported from the prototype's defaultDraft) */
export function defaultDraft() {
	return {
		type: null,
		source: null,
		// watch mode: 'all' = dub every new upload (default); 'filtered' = only
		// uploads matching the rules below. UI-only — not sent to the backend
		// (an 'all' automation simply ships empty filters, which match everything).
		filterMode: 'all',
		filters: {
			titleKeywords: [], titleMode: 'any', tags: [], playlists: [],
			dateFrom: '', dateTo: '', minViews: '', durMin: '', durMax: '', shorts: 'include',
		},
		deselectedIds: [],          // backfill: video ids the user un-checked (exclusion set)
		langs: [],
		publishMode: 'new_video',
		mapping: {},                 // { langCode: [destChannelId, …] }
		voiceByLang: {},             // { langCode: speakerId }
		voiceMeta: {},               // { speakerId: voiceObject } — card display cache, not sent
		publish: { privacy: 'public', translateMeta: true, copyTags: true },
		review: true,
		capMonth: 200000,
		name: '',
	};
}

/* ---- mid-builder OAuth round-trip ----
   The Google consent screen is a full-page redirect, so the in-progress
   draft is parked in sessionStorage and restored when the backend sends
   the user back with ?channel_connect=... */
const BUILDER_STATE_KEY = 'autodub.builderDraft.v1';
const BUILDER_STATE_TTL = 30 * 60 * 1000; // 30 min

export function saveBuilderState(state) {
	try {
		sessionStorage.setItem(BUILDER_STATE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
	} catch { /* storage full / unavailable — the user just restarts the builder */ }
}

/* Memoized per page-load: React 18 StrictMode double-invokes lazy useState
   initializers in dev, and the key is removed on first read. */
let _pendingLoaded;

export function loadPendingBuilderState() {
	// only restore when we're landing back from the OAuth redirect
	if (!window.location.search.includes('channel_connect')) {
		try { sessionStorage.removeItem(BUILDER_STATE_KEY); } catch { /* empty */ }
		return null;
	}
	if (_pendingLoaded !== undefined) return _pendingLoaded;
	try {
		const raw = sessionStorage.getItem(BUILDER_STATE_KEY);
		sessionStorage.removeItem(BUILDER_STATE_KEY);
		const state = raw ? JSON.parse(raw) : null;
		_pendingLoaded = (state?.savedAt && Date.now() - state.savedAt <= BUILDER_STATE_TTL) ? state : null;
	} catch {
		try { sessionStorage.removeItem(BUILDER_STATE_KEY); } catch { /* empty */ }
		_pendingLoaded = null;
	}
	return _pendingLoaded;
}

export default autoDubService;
